import constants from '../../../../../../../appConstants';
import { NumericEditor } from '@dataGrid/components/cellEditors';
import {
  SpecialNeedsRenderer,
  getSpecialNeeds
} from '@dataGrid/components/cellRenderer/index';
import {
  mergeUserConfig,
  formatMonthDateYearWithoutUTC
} from '@dataGrid/gridUtilities';
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter
} from '@dataGrid/components/columnFilters';
import PickupIndicatorIconRenderer from './cellRenderer/PickupIndicatorIconRenderer';
import PickupTypesRenderer from '../../Grid/cellRenderer/PickupTypesRenderer';
import PickupNumberRenderer from './cellRenderer/PickupNumberCellRenderer';
import { getZoneValue } from '../../../utils/index';
import { updateGridColumnHeaderNames } from '../../../../utils/utils';
import { gridShortHeaderNamesConfig } from '../../Grid/constants';
import PriorityRenderer from '../../Grid/cellRenderer/PriorityRenderer';
import { pickupTypeValueGetter } from '../../PickupManagement/utils';

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({
    userConfig,
    showStaticRoute,
    featureFlags: { enableColumnShortForm }
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration(showStaticRoute);
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig
    });
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm
    });
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getPickupTypeOptions(params) {
    return params.context.pickupTypeOptions;
  }

  static getNonZeroValue = (params) => {
    return params.data[params.column.colId] || '';
  };

  static getPackageTypes = (params) => {
    return params.data.packageType.join(',');
  };

  static getRouteNameColumns(showStaticRoute) {
    const driverRouteColumns = {
      headerName: 'Driver Route',
      headerTooltip: 'Driver Route Name',
      field: 'routeName',
      minWidth: constants.columnWidth.DRIVER_ROUTE,
      cellClass: 'gridCell-leftAlign',
      filter: MultiSelectFilter,
      hide: true
    };
    if (showStaticRoute) {
      return [
        {
          headerName: 'Route',
          headerTooltip: 'Route Name',
          field: 'staticRoute',
          minWidth: constants.columnWidth.W_90,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        { ...driverRouteColumns }
      ];
    }
    return [
      {
        ...driverRouteColumns,
        hide: false
      }
    ];
  }

  static getDefaultConfiguration(showStaticRoute) {
    const routeNameColumns = GridConfig.getRouteNameColumns(showStaticRoute);
    return {
      title: constants.gridNames.PickupsGrid,
      keyColumn: 'pickupId',
      rowSelection: 'single',
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.MERGED_PICKUP_ICON,
          maxWidth: constants.columnWidth.MERGED_PICKUP_ICON,
          pinned: "left",
          field: "isMergedPickup",
          lockPosition: true,
          lockPinned: true,
          cellClass: ["gridCell-centerAlign", "gridCell-merge"],
          filter: false,
          cellRenderer: PickupIndicatorIconRenderer,
          isInvisibleInShowHide: true,
        },
        {
          headerName: 'Pickup Number',
          headerTooltip: 'Pickup Number',
          field: 'pickupNumber',
          minWidth: constants.columnWidth.W_150,
          cellClass: 'gridCell-leftAlign',
          pinned: 'left',
          lockPosition: true,
          lockPinned: true,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING
          },
          cellRenderer: PickupNumberRenderer
        },
        {
          headerName: 'Zone',
          headerTooltip: 'Zone',
          valueGetter: getZoneValue,
          minWidth: constants.columnWidth.DISPATCH_ZONE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            addDefaultBlank: true
          }
        },
        {
          headerName: 'Driver',
          headerTooltip: 'Driver Name',
          field: 'driverName',
          minWidth: constants.columnWidth.DISPATCH_DRIVER_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        ...routeNameColumns,
        {
          headerName: 'Type',
          headerTooltip: 'Type',
          valueGetter: pickupTypeValueGetter,
          minWidth: constants.columnWidth.PICKUP_TYPE,
          cellClass: 'gridCell-leftAlign',
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getPickupTypeOptions
          },
          cellRenderer: PickupTypesRenderer
        },
        {
          headerName: 'Shipper Name',
          headerTooltip: 'Shipper Name',
          field: 'shipperName',
          minWidth: constants.columnWidth.CONSIGNEE_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'shipperName'
        },
        {
          headerName: 'Address',
          field: 'shipperAddress',
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          headerTooltip: 'Shipper Address',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'shipperAddress'
        },
        {
          headerName: 'City',
          field: 'shipperCity',
          headerTooltip: 'Shipper City',
          minWidth: constants.columnWidth.CITY,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'State',
          field: 'shipperState',
          headerTooltip: 'Shipper State',
          minWidth: constants.columnWidth.STATE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Zip',
          field: 'shipperZip',
          headerTooltip: 'Shipper Zip',
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Pickup Date',
          field: 'pickupDate',
          headerTooltip: 'Pickup Date',
          minWidth: constants.columnWidth.PICKUP_DATE,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: (params) => formatMonthDateYearWithoutUTC(params),
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.MONTHDATEYEAR
          }
        },
        {
          headerName: 'Ready Time',
          field: 'pickupReadyTime',
          headerTooltip: 'Ready Time',
          minWidth: constants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true
          }
        },
        {
          headerName: 'Close Time',
          field: 'pickupCloseTime',
          headerTooltip: 'Close Time',
          minWidth: constants.columnWidth.W_115,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME
          }
        },
        {
          headerName: 'Priority',
          minWidth: constants.columnWidth.PRIORITY,
          field: 'pickupPriority',
          headerTooltip: 'Priority',
          cellClass: 'gridCell-leftAlign',
          cellRenderer: (params) => <PriorityRenderer {...params} variant="tooltip" />,
          filter: MultiSelectFilter,
          filterParams: {
            cellRenderer: PriorityRenderer
          }
        },
        {
          headerName: 'Spl Needs',
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: getSpecialNeeds,
          headerTooltip: 'Special Needs',
          cellClass: 'gridCell-leftAlign',
          cellRenderer: SpecialNeedsRenderer,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption
          }
        },
        {
          headerName: 'Bills',
          field: 'bills',
          minWidth: constants.columnWidth.BILLS,
          headerTooltip: 'Bills',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Pieces',
          field: 'pieces',
          minWidth: constants.columnWidth.W_85,
          headerTooltip: 'Pieces',
          cellClass: 'gridCell-leftAlign',
          valueGetter: GridConfig.getNonZeroValue,
          filter: MultiSelectFilter
        },
        {
          headerName: 'HU',
          field: 'hu',
          minWidth: constants.columnWidth.W_80,
          headerTooltip: 'Handling Unit',
          cellClass: 'gridCell-leftAlign',
          valueGetter: GridConfig.getNonZeroValue,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Pkg Type',
          valueGetter: GridConfig.getPackageTypes,
          minWidth: constants.columnWidth.PKG_TYPE,
          headerTooltip: 'Package Type',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Weight',
          field: 'weight',
          minWidth: constants.columnWidth.W_90,
          headerTooltip: 'Weight',
          cellClass: 'gridCell-centerAlign',
          cellEditor: NumericEditor,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Special Instruction',
          field: 'specialInstructions',
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: 'Special Instructions',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'specialInstructions'
        },
        {
          headerName: 'Driver Number',
          headerTooltip: 'Driver Number',
          field: 'driverNumber',
          minWidth: constants.columnWidth.DRIVER_NUMBER,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING
          }
        }
      ]
    };
  }
}
